import React, { useCallback, useEffect } from 'react'
import { View } from 'react-native'
import useSession, { useCustomer } from 'hooks/useSession'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { contactModalSchema } from '@user/customer/contactSchemas'
import { markDetailSectionSaved } from '@user/customer/confirmation/markDetailSectionSaved'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { trpc } from 'api'
import FormInput from '@ui/forms/Input/FormInput'
import z from 'zod'
import { RegionPrefixes } from '@ca/common'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'

interface Props {
  open: boolean
  close: () => void
}

export default function ContactModal({ open, close }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'user'])
  const customer = useCustomer()

  const defaultValues = {
    email: customer?.email || undefined,
    mobile: customer?.mobile || undefined,
    phone: customer?.phone || undefined,
  }

  const form = useForm({
    defaultValues,
    resolver: zodResolver(contactModalSchema),
  })

  const isChanged = form.formState.isDirty
  const styleChanged = useAnimatedOpacity(isChanged)

  const setCustomerData = useSession((s) => s.setCustomerData)
  const patch = trpc.customer.patch.useMutation({
    onSuccess: (res) => {
      setCustomerData(res)
      markDetailSectionSaved('contact')
      onClose()
    },
  })

  const onSubmit = form.handleSubmit((values) => {
    if (!customer) return
    patch.mutate({ id: customer.id, ...values })
  })

  const onClose = () => {
    form.reset()
    close()
  }

  useEffect(() => {
    if (open) {
      form.reset(defaultValues)
    }
  }, [open])

  const config = useRemoteConfig()

  const prefix =
    RegionPrefixes[config?.region.countryCode || 'de'] || RegionPrefixes.de

  const mobileBlur = () => {
    if (form.getValues().mobile?.trim() === prefix) {
      form.setValue('mobile', '')
    }
  }

  const mobileFocus = () => {
    if (
      form.getValues().mobile?.trim() === prefix ||
      form.getValues().mobile?.trim() === ''
    ) {
      form.setValue('mobile', `${prefix} `)
    }
  }

  return (
    <ModalBottom
      open={open}
      close={close}
      title={t('modals:contact.title')}
      desc={t('modals:contact.desc')}
      body={
        <FormProvider {...form}>
          <View className="gap-6 px-16">
            <FormInput
              control={form.control}
              label={t('user:email')}
              name="email"
            />
            <FormInput
              control={form.control}
              label={t('user:mobile')}
              onBlur={mobileBlur}
              onFocus={mobileFocus}
              name="mobile"
            />
            <FormInput
              control={form.control}
              label={t('user:landline')}
              name="phone"
            />
          </View>
        </FormProvider>
      }
      buttons={
        <>
          <Button variant="outlined" label={t('ui:close')} onPress={onClose} />
          {isChanged && (
            <Button
              variant="filled"
              label={t('ui:save')}
              iconEnd="arrow-narrow-right"
              loading={patch.isPending}
              onPress={onSubmit}
              style={styleChanged}
            />
          )}
        </>
      }
    />
  )
}
